import React, { useEffect, useState, useRef } from 'react';
import { formatPrice } from '../utils/utils';
import {
    Box,
    Typography,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Chip,
    IconButton,
    Tooltip,
    Card,
    CardContent,
    Alert,
    CircularProgress,
    TextField,
    Button,
    Divider,
    Stack,
    Switch,
    FormControlLabel,
    TablePagination,
    ToggleButton,
    ToggleButtonGroup,
    FormControl,
    InputLabel,
    Select,
    MenuItem
} from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import { useLivePrices } from '../hooks/useLivePrices';
import {
    Refresh as RefreshIcon,
    Info as InfoIcon,
    TrendingUp,
    TrendingDown,
    EventNote,
    AutoGraph,
    Settings as SettingsIcon,
    Save as SaveIcon,
    PowerSettingsNew as EmergencyIcon,
    PlayArrow as ResumeIcon,
    ShowChart as ShowChartIcon
} from '@mui/icons-material';
import {
    getTodayAutoTradeLogs,
    getAutoTradeStatus,
    getPnlHeatMap,
    updateAutoTradeSetting,
    emergencyStop,
    resumeTrading
} from '../services/autoTradeService';
import PnLHeatMap from '../components/PnLHeatMap';
import { toast } from 'react-toastify';
import moment from 'moment';
import { classNames } from '../utils/utils';
import api from '../services/api';
import { useAuth } from '../hooks/useAuth';
import ZerodhaAuthAlert from '../components/ZerodhaAuthAlert';

const TradeDashboard: React.FC = () => {
    const [logs, setLogs] = useState<any[]>([]);
    const [status, setStatus] = useState<any>(null);
    const [chartTrade, setChartTrade] = useState<any>(null);
    const [heatMapData, setHeatMapData] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [capitalValue, setCapitalValue] = useState<string>("");
    const [viewMode, setViewMode] = useState<'paper' | 'live'>('paper');
    const [selectedDate, setSelectedDate] = useState<string>(moment().utcOffset('+05:30').format('YYYY-MM-DD'));
    const [emergencyDialogOpen, setEmergencyDialogOpen] = useState(false);
    const [isHalted, setIsHalted] = useState(false);
    const { user, isAdmin } = useAuth();

    // Pagination state
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(20);
    const [totalLogs, setTotalLogs] = useState(0);
    const [backendStats, setBackendStats] = useState<any>(null);
    const [filter, setFilter] = useState<'ALL' | 'TRADES' | 'OPEN' | 'CLOSED' | 'REJECTED'>('TRADES');
    const initialSyncDone = useRef(false);

    // Market hours check: 9:15 AM to 3:30 PM IST
    const isMarketHours = () => {
        const now = moment().utcOffset('+05:30');
        const marketOpen = moment().utcOffset('+05:30').set({ hour: 9, minute: 15, second: 0 });
        const marketClose = moment().utcOffset('+05:30').set({ hour: 15, minute: 30, second: 0 });
        return now.isBetween(marketOpen, marketClose);
    };

    const { prices, getLivePrice } = useLivePrices();

    const fetchData = async () => {
        setLoading(true);
        try {
            const [logsResponse, statusData, heatData] = await Promise.all([
                getTodayAutoTradeLogs(viewMode, selectedDate, page + 1, rowsPerPage, filter),
                getAutoTradeStatus(),
                getPnlHeatMap(365, viewMode)
            ]);

            const fetchedLogs = Array.isArray(logsResponse.logs) ? logsResponse.logs : [];
            setLogs(fetchedLogs);
            setTotalLogs(logsResponse.pagination?.total || 0);
            setBackendStats(logsResponse.stats || null);

            setStatus(statusData);
            setIsHalted(statusData.isHalted || false);
            setHeatMapData(Array.isArray(heatData) ? heatData : []);
            setCapitalValue(statusData?.config?.totalCapital?.toString() || "");

            // Sync viewMode with live execution status ONLY on initial load
            // Use userAutoTradeEnabled which reflects the logged-in user's own preference
            if (!initialSyncDone.current && statusData) {
                const liveEnabled = (statusData.userAutoTradeEnabled ?? statusData.enabled) === true;
                setViewMode(liveEnabled ? 'live' : 'paper');
                initialSyncDone.current = true;
            }

            // Subscribe to live prices for ALL non-rejected trades on this page + any open trades
            const openTradesSummary = logsResponse.stats?.openTrades || [];
            const openTokens = openTradesSummary
                .filter((t: any) => t.instrument_token)
                .map((t: any) => Number(t.instrument_token));
            const pageTokens = fetchedLogs
                .filter((t: any) => t.status !== 'REJECTED' && t.instrument_token)
                .map((t: any) => Number(t.instrument_token));

            const tokens = [...new Set([...openTokens, ...pageTokens])];
            if (tokens.length > 0) {
                getLivePrice(tokens);
            }

        } catch (error: any) {
            console.error("Error fetching dashboard data", error);
            if (error.response?.data?.code === "NO_AUTH_TOKEN") {
                // Handle 401
            } else {
                toast.error("Failed to load dashboard data");
            }
        }
        setLoading(false);
    };

    const handleToggleLive = async (event: React.ChangeEvent<HTMLInputElement>) => {
        if (isMarketHours()) {
            toast.error("Cannot switch trading mode during market hours (9:15-15:30)");
            return;
        }
        const newValue = event.target.checked;
        try {
            await updateAutoTradeSetting("auto_trading_enabled", newValue);
            setStatus((prev: any) => ({ ...prev, enabled: newValue }));
            setViewMode(newValue ? 'live' : 'paper');
            toast.success(`Live Trading ${newValue ? "Enabled" : "Disabled"}`);
        } catch (error) {
            const msg = (error as any)?.response?.status === 403
                ? "Admin access required to change trading mode"
                : "Failed to update trading mode";
            toast.error(msg);
        }
    };

    const handleEmergencyStop = async () => {
        try {
            const result = await emergencyStop();
            setIsHalted(true);
            setStatus((prev: any) => ({ ...prev, enabled: false }));
            setEmergencyDialogOpen(false);
            toast.warning(`Emergency stop: ${result.closedCount} trades closed`);
            fetchData();
        } catch (error) {
            toast.error("Emergency stop failed!");
        }
    };

    const handleResumeTrading = async () => {
        try {
            await resumeTrading();
            setIsHalted(false);
            toast.success("Trading resumed");
        } catch (error) {
            toast.error("Failed to resume trading");
        }
    };


    const handleUpdateCapital = async () => {
        const val = parseInt(capitalValue);
        if (isNaN(val) || val <= 0) {
            toast.error("Please enter a valid capital amount");
            return;
        }
        try {
            await updateAutoTradeSetting("total_capital", val);
            setStatus((prev: any) => ({
                ...prev,
                config: { ...prev.config, totalCapital: val }
            }));
            toast.success("Capital limit updated");
        } catch (error) {
            toast.error("Failed to update capital");
        }
    };


    useEffect(() => {
        fetchData();
        const interval = setInterval(fetchData, 30000); // 30 sec auto-refresh
        return () => clearInterval(interval);
    }, [viewMode, selectedDate, page, rowsPerPage, filter]); // Refetch when pagination, view, or filter changes

    const calculatePnL = () => {
        if (!backendStats) return 0;
        const closedPnL = backendStats.totalPnL || 0;
        const realizedOpenPnL = backendStats.realizedPnL || 0;

        const unrealizedPnL = (backendStats.openTrades || []).reduce((sum: number, ot: any) => {
            const avgEntry = ot.avgEntryPrice || ot.entryPrice;
            const currentPrice = prices[Number(ot.instrument_token)]?.price || avgEntry;
            const currentQty = ot.positionSize - (ot.partialExitQty || 0);
            const direction = (ot.direction || ot.type || "").toString().toUpperCase().trim();
            const isShort = direction === 'SHORT' || direction === 'SELL';
            const priceDiff = isShort ? (avgEntry - currentPrice) : (currentPrice - avgEntry);
            return sum + (priceDiff * currentQty);
        }, 0);

        // Realized PnL from partial exits is already in backendStats.realizedPnL
        return closedPnL + realizedOpenPnL + unrealizedPnL;
    };

    const calculateTotalCharges = () => {
        return backendStats?.totalCharges || 0;
    };

    const calculateNetPnL = () => {
        return calculatePnL() - calculateTotalCharges();
    };

    const getOpenTradePnL = (log: any) => {
        const livePrice = prices[Number(log.instrument_token)]?.price || (log.avgEntryPrice || log.entryPrice);
        const entryPrice = log.avgEntryPrice || log.entryPrice;
        const qty = log.positionSize - (log.partialExitQty || 0);
        const direction = (log.direction || log.type || "").toString().toUpperCase().trim();
        const isShort = direction === 'SHORT' || direction === 'SELL';

        const priceDiff = isShort ? (entryPrice - livePrice) : (livePrice - entryPrice);
        return (priceDiff * qty) + (log.realizedPnL || 0);
    };

    const getOpenTradePnLPct = (log: any) => {
        const livePrice = prices[Number(log.instrument_token)]?.price || (log.avgEntryPrice || log.entryPrice);
        const entryPrice = log.avgEntryPrice || log.entryPrice;
        const direction = (log.direction || log.type || "").toString().toUpperCase().trim();
        const isShort = direction === 'SHORT' || direction === 'SELL';

        const priceDiff = isShort ? (entryPrice - livePrice) : (livePrice - entryPrice);
        const unrealizedPnL = priceDiff * (log.positionSize - (log.partialExitQty || 0));
        const totalPnL = unrealizedPnL + (log.realizedPnL || 0);
        return (totalPnL / (entryPrice * log.positionSize)) * 100;
    };

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'OPEN': return 'primary';
            case 'CLOSED': return 'success';
            case 'FAILED': return 'error';
            case 'CANCELLED': return 'warning';
            default: return 'default';
        }
    };

    const handleViewChart = (trade: any) => {
        setChartTrade(trade);
    };

    const handleCloseChart = () => {
        setChartTrade(null);
    };

    const columns: GridColDef[] = React.useMemo(() => [
        {
            field: 'actions',
            headerName: 'Chart',
            width: 60,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params) => {
                if (params.row.status !== 'CLOSED') return '-';
                return (
                    <Tooltip title="View Chart">
                        <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleViewChart(params.row); }} sx={{ p: 0.5 }}>
                            <ShowChartIcon fontSize="small" color="primary" />
                        </IconButton>
                    </Tooltip>
                );
            }
        },
        {
            field: 'symbol',
            headerName: 'Symbol',
            width: 130,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params) => (
                <Box sx={{ py: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100%', textAlign: 'center' }}>
                    <Typography variant="body2" fontWeight="bold">{params.row.symbol}</Typography>
                    <Typography variant="caption" color="textSecondary">
                        {moment(params.row.entryTime).utcOffset('+05:30').format('HH:mm:ss')} IST
                    </Typography>
                </Box>
            )
        },
        {
            field: 'direction',
            headerName: 'Dir',
            width: 80,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params) => (
                <Chip
                    label={params.value || 'LONG'}
                    size="small"
                    color={params.value === 'SHORT' ? 'error' : 'success'}
                    variant="filled"
                    sx={{ fontWeight: 'bold', minWidth: '65px' }}
                />
            )
        },
        {
            field: 'grade',
            headerName: 'Grade',
            width: 70,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params) => (
                <Chip
                    label={params.value}
                    color={
                        params.value === "A+"
                            ? "success"
                            : params.value === "A"
                                ? "info"
                                : params.value === "B"
                                    ? "warning"
                                    : "default"
                    }
                    size="small"
                />
            )
        },
        {
            field: 'strategyUsed',
            headerName: 'Strategy',
            width: 140,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params) => {
                const name = params.value || '-';
                const isORB = name.includes('ORB');
                const isMR = name.includes('MEAN_REVERSAL') || name.includes('Mean Reversal');
                const isTrend = name.includes('TREND_FOLLOWING') || name.includes('Trend Following');
                const isPOM = name.includes('POM');

                return (
                    <Chip
                        label={name.split(' [')[0]}
                        size="small"
                        color={isORB ? "primary" : isTrend ? "secondary" : isMR ? "info" : isPOM ? "warning" : "default"}
                        variant="outlined"
                        sx={{ fontWeight: 'medium' }}
                    />
                );
            }
        },
        {
            field: 'status',
            headerName: 'Status',
            width: 100,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params) => (
                <Chip
                    label={params.value}
                    size="small"
                    color={getStatusColor(params.value)}
                    variant="outlined"
                />
            )
        },
        {
            field: 'liveTicker',
            headerName: 'Live Ticker',
            width: 120,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params) => {
                const log = params.row;
                if (log.status === 'REJECTED') return '-';

                const token = Number(log.instrument_token);
                const liveData = prices?.[token];
                const livePrice = liveData?.price;
                const displayPrice = livePrice || log.entryPrice || 0;

                let pctValue: string = "0.00";
                let isUp = true;
                let isNeutral = true;

                if (liveData?.change !== undefined) {
                    pctValue = liveData.change.toFixed(2);
                    isUp = liveData.change >= 0;
                    isNeutral = false;
                } else if (livePrice && log.entryPrice) {
                    const diff = livePrice - log.entryPrice;
                    pctValue = ((diff / log.entryPrice) * 100).toFixed(2);
                    isUp = diff >= 0;
                    isNeutral = false;
                }

                return (
                    <Box sx={{ py: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100%', textAlign: 'center' }}>
                        <div className={classNames(isNeutral ? "" : (isUp ? "upColor" : "downColor"), "percentCt")} style={{ width: 'auto', justifyContent: 'center' }}>
                            <Typography
                                variant="body2"
                                fontWeight="bold"
                                className={isNeutral ? "" : (isUp ? "priceUp" : "priceDown")}
                                sx={{ color: 'inherit !important', fontSize: '14px' }}
                            >
                                {formatPrice(displayPrice)}
                            </Typography>
                        </div>
                        {!isNeutral && (
                            <Typography
                                variant="caption"
                                fontWeight="bold"
                                className={isUp ? "percentUp" : "percentDown"}
                                sx={{ display: 'block', ml: 0 }}
                            >
                                {isUp ? '+' : ''}{pctValue}
                            </Typography>
                        )}
                    </Box>
                );
            }
        },
        {
            field: 'entryPrice',
            headerName: 'Entry',
            width: 100,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params) => (
                <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100%', textAlign: 'center' }}>
                    <Typography variant="body2">₹{formatPrice(Number(params.value || 0))}</Typography>
                    {params.row.avgEntryPrice && params.row.avgEntryPrice !== params.value && (
                        <Typography variant="caption" color="textSecondary">
                            Avg: ₹{formatPrice(Number(params.row.avgEntryPrice))}
                        </Typography>
                    )}
                </Box>
            )
        },
        { field: 'positionSize', headerName: 'Qty', width: 70, align: 'center', headerAlign: 'center' },
        { field: 'stopLoss', headerName: 'SL', width: 90, align: 'center', headerAlign: 'center', valueFormatter: (value: any) => value ? `₹${formatPrice(Number(value))}` : '-' },
        { field: 'target', headerName: 'Target', width: 90, align: 'center', headerAlign: 'center', valueFormatter: (value: any) => value ? `₹${formatPrice(Number(value))}` : '-' },
        {
            field: 'exitPrice',
            headerName: 'Exit',
            width: 90,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params) => (
                <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100%', textAlign: 'center' }}>
                    <Typography variant="body2" fontWeight="medium">
                        {params.row.status === 'CLOSED' ? `₹${formatPrice(Number(params.value || 0))}` : '-'}
                    </Typography>
                </Box>
            )
        },
        {
            field: 'pnlPct',
            headerName: 'PnL %',
            width: 80,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params) => {
                const log = params.row;
                if (log.status === 'REJECTED') return '-';
                const pnlPct = log.status === 'OPEN' ? getOpenTradePnLPct(log) : (log.pnlPct || 0);
                return (
                    <Typography
                        variant="body2"
                        fontWeight="bold"
                        color={pnlPct >= 0 ? "success.main" : "error.main"}
                    >
                        {formatPrice(pnlPct)}%
                    </Typography>
                );
            }
        },
        {
            field: 'pnl',
            headerName: 'PnL',
            width: 110,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params) => {
                const log = params.row;
                if (log.status === 'REJECTED') return '-';
                const pnl = log.status === 'OPEN' ? getOpenTradePnL(log) : (log.pnl || 0);
                return (
                    <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100%', textAlign: 'center' }}>
                        <Typography
                            variant="body2"
                            fontWeight="bold"
                            color={pnl >= 0 ? "success.main" : "error.main"}
                        >
                            ₹{formatPrice(pnl)}
                            {log.status === 'OPEN' && log.isPartialExitTaken && (
                                <span style={{ fontSize: '10px', display: 'block', color: 'gray' }}>
                                    (R: ₹{formatPrice(Number(log.realizedPnL || 0))})
                                </span>
                            )}
                        </Typography>
                    </Box>
                );
            }
        },
        {
            field: 'charges',
            headerName: 'Charges',
            width: 100,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params) => {
                const log = params.row;
                if (log.status === 'REJECTED') return '-';
                const charges = log.charges || 0;
                return (
                    <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100%', textAlign: 'center' }}>
                        <Typography
                            variant="body2"
                            fontWeight="bold"
                            color="error.main"
                        >
                            - ₹{formatPrice(charges)}
                        </Typography>
                    </Box>
                );
            }
        },
        {
            field: 'netPnl',
            headerName: 'Net PnL',
            width: 100,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params) => {
                const log = params.row;
                if (log.status === 'REJECTED') return '-';

                let net = 0;
                if (log.status === 'CLOSED') {
                    net = log.netPnl ?? (log.pnl - (log.charges || 0));
                } else {
                    // For OPEN trades: getOpenTradePnL already includes realizedPnL from partials
                    net = getOpenTradePnL(log) - (log.charges || 0);
                }

                return (
                    <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100%', textAlign: 'center' }}>
                        <Typography
                            variant="body2"
                            fontWeight="bold"
                            color={net >= 0 ? "success.main" : "error.main"}
                        >
                            ₹{formatPrice(Number(net || 0))}
                        </Typography>
                    </Box>
                );
            }
        },

        {
            field: 'partial',
            headerName: 'Partial',
            width: 80,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params) => params.row.isPartialExitTaken ? (
                <Chip label="1R Hit" size="small" color="secondary" variant="filled" />
            ) : '-'
        },
        {
            field: 'reason',
            headerName: 'Reason',
            flex: 1,
            renderCell: (params) => (
                <Box sx={{ display: 'flex', alignItems: 'center', height: '100%', justifyContent: 'flex-start' }}>
                    {params.row.checklogs && params.row.checklogs.length > 0 && (
                        <Tooltip title={
                            <Box>
                                {params.row.checklogs.map((c: string, i: number) => (
                                    <div key={i}>{c}</div>
                                ))}
                            </Box>
                        }>
                            <IconButton size="small"><InfoIcon fontSize="inherit" /></IconButton>
                        </Tooltip>
                    )}
                    <Typography variant="caption" sx={{ mr: 1 }}>
                        {params.row.exitReason || params.row.entryReason}
                    </Typography>
                </Box>
            )
        }
    ], [prices]);

    return (
        <>
            <Box sx={{ p: 3 }}>
                {!user?.isZerodhaLinked && <ZerodhaAuthAlert />}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                    <Stack direction="row" spacing={2} alignItems="center">
                        <Typography variant="h4" fontWeight="bold">Trade Dashboard</Typography>
                        {status && (
                            <Chip
                                variant='outlined'
                                label={`${status.aggressionMode} MODE`}
                                color={status.aggressionMode === 'HUNTER' ? 'success' : status.aggressionMode === 'DEFENSIVE' ? 'info' : 'warning'}
                                icon={<AutoGraph />}
                                sx={{ fontWeight: 'bold' }}
                            />
                        )}
                    </Stack>

                    {status?.marketStats && (
                        <Paper variant="outlined" sx={{ px: 2, py: 0.5, display: 'flex', gap: 2, alignItems: 'center', borderRadius: 5, flexWrap: 'wrap' }}>
                            <Typography variant="caption" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                Gap:
                                <Box component="span" sx={{ color: status.marketStats.niftyGapPct >= 0 ? 'success.main' : 'error.main', fontWeight: 'bold' }}>
                                    {formatPrice(status.marketStats.niftyGapPct)}%
                                </Box>
                            </Typography>
                            <Divider orientation="vertical" flexItem />
                            <Typography variant="caption" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                5m Range:
                                <Box component="span" sx={{ color: status.marketStats.indexRange5mPct > 1 ? 'error.main' : 'text.secondary', fontWeight: 'bold' }}>
                                    {formatPrice(status.marketStats.indexRange5mPct)}%
                                </Box>
                            </Typography>
                            <Divider orientation="vertical" flexItem />
                            <Typography variant="caption" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                Breadth:
                                <Box component="span" sx={{ color: status.marketStats.breadthRatio > 1.5 ? 'success.main' : status.marketStats.breadthRatio < 0.7 ? 'error.main' : 'text.secondary', fontWeight: 'bold' }}>
                                    {formatPrice(status.marketStats.breadthRatio)}
                                </Box>
                            </Typography>
                            <Divider orientation="vertical" flexItem />
                            <Typography variant="caption" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                ATR Exp:
                                <Box component="span" sx={{ color: status.marketStats.atrExpansion > 1.3 ? 'error.main' : 'text.secondary', fontWeight: 'bold' }}>
                                    {formatPrice(status.marketStats.atrExpansion)}x
                                </Box>
                            </Typography>
                            <Divider orientation="vertical" flexItem />
                            <Typography variant="caption" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                VIX:
                                <Box component="span" sx={{ color: status.marketStats.vix > 18 ? 'warning.main' : 'text.secondary', fontWeight: 'bold' }}>
                                    {formatPrice(status.marketStats.vix)}
                                </Box>
                            </Typography>
                        </Paper>
                    )}

                    <Stack direction="row" spacing={2} alignItems="center">
                        {/* Paper/Live Toggle */}
                        <Box sx={{ display: 'flex', alignItems: 'center', bgcolor: 'background.paper', borderRadius: 2, px: 1 }}>
                            Show Logs:
                            <Typography
                                variant="body2"
                                sx={{ fontWeight: viewMode === 'paper' ? 'bold' : 'normal', color: viewMode === 'paper' ? 'warning.main' : 'text.secondary' }}
                            >
                                Paper
                            </Typography>
                            <Switch
                                checked={viewMode === 'live'}
                                onChange={(e) => setViewMode(e.target.checked ? 'live' : 'paper')}
                                color="success"
                            />
                            <Typography
                                variant="body2"
                                sx={{ fontWeight: viewMode === 'live' ? 'bold' : 'normal', color: viewMode === 'live' ? 'success.main' : 'text.secondary' }}
                            >
                                Live
                            </Typography>
                        </Box>
                        <IconButton onClick={fetchData} color="primary">
                            <RefreshIcon />
                        </IconButton>
                    </Stack>
                </Box>

                {/* Top Section: Alert + Settings (Left) | Heatmap (Right) */}
                <Box sx={{ mb: 2 }}>
                    {status?.marketStats?.indexHalt && (
                        <Alert severity="warning" sx={{ mb: 2, fontWeight: 'bold' }}>
                            {status.marketStats.indexHaltReason}
                        </Alert>
                    )}
                </Box>
                <Box sx={{ display: 'flex', gap: 3, mb: 4, flexWrap: { xs: 'wrap', lg: 'nowrap' } }}>
                    {/* Left Section: Alert + Settings */}
                    <Box sx={{ flex: '0.5', width: { xs: '100%', lg: '320px' }, display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {status && (
                            <>
                                <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                    {isAdmin && (
                                        <>
                                            <Stack direction="row" justifyContent="space-between" alignItems="center">
                                                <Tooltip title={isMarketHours() ? "Cannot switch during market hours (9:15-15:30)" : "Toggle between real and virtual order placement"}>
                                                    <span>
                                                        <FormControlLabel
                                                            control={<Switch checked={status.enabled} onChange={handleToggleLive} color="success" disabled={isMarketHours()} />}
                                                            label={<Typography variant="body2" fontWeight="bold">Live Execution</Typography>}
                                                        />
                                                    </span>
                                                </Tooltip>
                                                <Tooltip title="Toggle between real and virtual order placement">
                                                    <IconButton size="small"><SettingsIcon fontSize="small" /></IconButton>
                                                </Tooltip>
                                            </Stack>
                                            <Divider />
                                            <Stack direction="row" spacing={1} alignItems="center">
                                                <TextField
                                                    label="Base Capital (₹)"
                                                    size="small"
                                                    type="number"
                                                    value={capitalValue}
                                                    onChange={(e) => setCapitalValue(e.target.value)}
                                                    sx={{ flex: 1 }}
                                                />
                                                <Button
                                                    variant="contained"
                                                    size="small"
                                                    onClick={handleUpdateCapital}
                                                    startIcon={<SaveIcon />}
                                                >
                                                    Save
                                                </Button>
                                            </Stack>
                                            <Divider />
                                            <Divider />
                                            {/* Emergency Stop / Resume */}
                                            {isHalted ? (
                                                <Button
                                                    variant="contained"
                                                    color="success"
                                                    fullWidth
                                                    startIcon={<ResumeIcon />}
                                                    onClick={handleResumeTrading}
                                                >
                                                    Resume Trading
                                                </Button>
                                            ) : (
                                                <Button
                                                    variant="contained"
                                                    color="error"
                                                    fullWidth
                                                    startIcon={<EmergencyIcon />}
                                                    onClick={() => setEmergencyDialogOpen(true)}
                                                >
                                                    Emergency Stop
                                                </Button>
                                            )}
                                            {isHalted && (
                                                <Alert severity="error" sx={{ py: 0.5 }}>
                                                    🚨 EMERGENCY HALT active — all new entries blocked
                                                </Alert>
                                            )}
                                        </>
                                    )}
                                    {!isAdmin && (
                                        <Alert severity="info" sx={{ py: 0.5 }}>
                                            Mode: {status.userAutoTradeEnabled ? '🟢 Live' : '📝 Paper'} — Admin controls trading mode
                                        </Alert>
                                    )}
                                </Paper>
                            </>
                        )}
                    </Box>

                    {/* Right Section: Heatmap */}
                    <Box sx={{ flex: 1, minWidth: 0, overflowX: 'auto' }}>
                        {/* <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                        <Typography variant="subtitle2" color="textSecondary">PnL Heatmap</Typography>
                        <Chip 
                            label={viewMode === 'paper' ? 'Paper' : 'Live'}
                            size="small"
                            color={viewMode === 'paper' ? 'warning' : 'success'}
                            variant="outlined"
                        />
                    </Stack> */}
                        <PnLHeatMap
                            data={heatMapData}
                            viewMode={viewMode}
                            selectedDate={selectedDate}
                            onDateClick={(date) => setSelectedDate(date)}
                        />
                    </Box>
                </Box>

                <Stack direction={{ xs: 'column', md: 'row' }} spacing={3} sx={{ mb: 4 }}>
                    <Box sx={{ flex: 1 }}>
                        <Card sx={{ bgcolor: 'background.paper', borderRadius: 2 }}>
                            <CardContent>
                                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                                    <Typography variant="subtitle2" color="textSecondary">{moment(selectedDate).format('MMM D')} Net PnL</Typography>
                                    <Chip
                                        label={viewMode === 'paper' ? 'Paper' : 'Live'}
                                        size="small"
                                        color={viewMode === 'paper' ? 'warning' : 'success'}
                                        variant="outlined"
                                    />
                                </Stack>
                                <Typography variant="h4" color={calculateNetPnL() >= 0 ? "success.main" : "error.main"}>
                                    ₹{formatPrice(calculateNetPnL(), 0)}
                                </Typography>
                                <Stack direction="row" spacing={2} sx={{ mt: 1 }}>
                                    <Typography variant="caption" color="textSecondary">
                                        Gross PnL: ₹{formatPrice(calculatePnL(), 0)}
                                    </Typography>
                                    <Typography variant="caption" color="textSecondary">
                                        Charges: ₹{formatPrice(calculateTotalCharges(), 0)}
                                    </Typography>
                                </Stack>
                            </CardContent>
                        </Card>
                    </Box>
                    <Box sx={{ flex: 1 }}>
                        <Card sx={{ bgcolor: 'background.paper', borderRadius: 2 }}>
                            <CardContent>
                                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                                    <Typography variant="subtitle2" color="textSecondary">Active Trades</Typography>
                                    <Chip
                                        label={viewMode === 'paper' ? 'Paper' : 'Live'}
                                        size="small"
                                        color={viewMode === 'paper' ? 'warning' : 'success'}
                                        variant="outlined"
                                    />
                                </Stack>
                                <Typography variant="h4">
                                    {backendStats?.activeCount || 0}
                                </Typography>
                            </CardContent>
                        </Card>
                    </Box>
                    <Box sx={{ flex: 1 }}>
                        <Card sx={{ bgcolor: 'background.paper', borderRadius: 2 }}>
                            <CardContent>
                                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                                    <Typography variant="subtitle2" color="textSecondary">Closed Trades</Typography>
                                    <Chip
                                        label={viewMode === 'paper' ? 'Paper' : 'Live'}
                                        size="small"
                                        color={viewMode === 'paper' ? 'warning' : 'success'}
                                        variant="outlined"
                                    />
                                </Stack>
                                <Typography variant="h4">
                                    {backendStats?.closedCount || 0}
                                </Typography>
                            </CardContent>
                        </Card>
                    </Box>
                    <Box sx={{ flex: 1 }}>
                        <Card sx={{ bgcolor: 'background.paper', borderRadius: 2 }}>
                            <CardContent>
                                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                                    <Typography variant="subtitle2" color="textSecondary">Rejected</Typography>
                                </Stack>
                                <Typography variant="h4" color="text.secondary">
                                    {backendStats?.rejectedCount || 0}
                                </Typography>
                            </CardContent>
                        </Card>
                    </Box>
                </Stack>

                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                    <Typography sx={{ mb: 2 }} variant="h6" color="textSecondary">
                        {moment(selectedDate).format('MMM D, YYYY')} {viewMode === 'paper' ? 'Paper' : 'Live'} Execution Logs
                        {selectedDate === moment().utcOffset('+05:30').format('YYYY-MM-DD') && " (Today)"}
                    </Typography>
                    <Chip
                        label={viewMode === 'paper' ? 'Paper' : 'Live'}
                        size="small"
                        color={viewMode === 'paper' ? 'warning' : 'success'}
                        variant="outlined"
                    />
                </Stack>

                <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <ToggleButtonGroup
                        value={filter}
                        exclusive
                        onChange={(e, val) => {
                            if (val !== null) {
                                setFilter(val);
                                setPage(0);
                            }
                        }}
                        size="small"
                        color="primary"
                    >
                        <ToggleButton value="ALL" sx={{ px: 2, fontWeight: 'bold' }}>
                            All ({(backendStats?.activeCount || 0) + (backendStats?.closedCount || 0) + (backendStats?.rejectedCount || 0) + (backendStats?.failedCount || 0)})
                        </ToggleButton>
                        <ToggleButton value="TRADES" color="info" sx={{ px: 2, fontWeight: 'bold' }}>
                            Trades ({(backendStats?.activeCount || 0) + (backendStats?.closedCount || 0)})
                        </ToggleButton>
                        <ToggleButton value="OPEN" color="primary" sx={{ px: 2, fontWeight: 'bold' }}>
                            Active ({backendStats?.activeCount || 0})
                        </ToggleButton>
                        <ToggleButton value="CLOSED" color="success" sx={{ px: 2, fontWeight: 'bold' }}>
                            Closed ({backendStats?.closedCount || 0})
                        </ToggleButton>
                        <ToggleButton value="REJECTED" color="error" sx={{ px: 2, fontWeight: 'bold' }}>
                            Rejected ({backendStats?.rejectedCount || 0})
                        </ToggleButton>
                    </ToggleButtonGroup>
                </Box>

                {/* Trade Logs Table using DataGrid */}
                <Box sx={{ height: 750, width: '100%' }}>
                    <DataGrid
                        loading={loading}
                        rows={logs}
                        columns={columns}
                        getRowId={(row) => row._id}
                        paginationMode="server"
                        rowCount={totalLogs}
                        paginationModel={{ page, pageSize: rowsPerPage }}
                        onPaginationModelChange={(model) => {
                            setPage(model.page);
                            setRowsPerPage(model.pageSize);
                        }}
                        pageSizeOptions={[10, 20, 50, 100]}
                        disableRowSelectionOnClick
                        sx={{
                            bgcolor: 'background.paper',
                            borderRadius: 2,
                            '& .MuiDataGrid-columnHeaders': {
                                bgcolor: 'action.hover',
                            },
                            '& .MuiDataGrid-cell': {
                                display: 'flex',
                                alignItems: 'center',
                            },
                        }}
                        rowHeight={65}
                    />
                </Box>
            </Box>

            {/* Emergency Stop Confirmation Dialog */}
            <Dialog open={emergencyDialogOpen} onClose={() => setEmergencyDialogOpen(false)}>
                <DialogTitle sx={{ color: 'error.main', fontWeight: 'bold' }}>
                    🚨 Emergency Stop
                </DialogTitle>
                <DialogContent>
                    <Typography>This will immediately:</Typography>
                    <ul>
                        <li>Cancel all pending entry orders</li>
                        <li>Close all open positions at market price</li>
                        <li>Halt the system from taking new trades</li>
                    </ul>
                    <Typography fontWeight="bold" sx={{ mt: 2 }}>Are you sure you want to proceed?</Typography>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={() => setEmergencyDialogOpen(false)} variant="outlined">Cancel</Button>
                    <Button onClick={handleEmergencyStop} variant="contained" color="error">
                        CONFIRM EMERGENCY STOP
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Chart Dialog */}
            <Dialog open={!!chartTrade} onClose={handleCloseChart} maxWidth="md" fullWidth>
                <DialogTitle sx={{ m: 0, p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h6">{chartTrade?.symbol} Chart</Typography>
                    <IconButton
                        aria-label="close"
                        onClick={handleCloseChart}
                        size="small"
                    >
                        ✕
                    </IconButton>
                </DialogTitle>
                <DialogContent sx={{ p: 0, textAlign: 'center', bgcolor: '#131722', display: 'flex', justifyContent: 'center' }}>
                    {chartTrade && (
                        <img
                            src={`${import.meta.env.VITE_SWING_API_ENDPOINT}/chart/image?symbol=${chartTrade.symbol.includes(':') ? chartTrade.symbol : `NSE:${chartTrade.symbol}`}&entryPrice=${chartTrade.entryPrice}&stopLoss=${chartTrade.stopLoss || chartTrade.initialStopLoss || ''}&target=${chartTrade.target || ''}&exitPrice=${chartTrade.exitPrice || ''}&direction=${chartTrade.direction || 'LONG'}&entryTime=${chartTrade.entryTime || ''}&exitTime=${chartTrade.exitTime || ''}&interval=5m`}
                            alt={`Chart for ${chartTrade.symbol}`}
                            style={{ width: '100%', maxWidth: '800px', height: 'auto', display: 'block' }}
                        />
                    )}
                </DialogContent>
            </Dialog>
        </>
    );
};

export default TradeDashboard;
